import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { fetchUserAttributes } from "aws-amplify/auth";
import { createUser } from "../lib/api/users";
import { UserRole } from "../API";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { User, Upload, Camera } from "lucide-react";

interface ProfileFormData {
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  displayName: string;
}

const ProfileSetup: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthenticator();
  const [isLoading, setIsLoading] = useState(false);
  const [uploadSkipped, setUploadSkipped] = useState(false);
  const [googleProfilePicture, setGoogleProfilePicture] = useState<
    string | null
  >(null);
  const [formData, setFormData] = useState<ProfileFormData>({
    username: "",
    email: "",
    firstName: "",
    lastName: "",
    displayName: "",
  });

  // Initialize form with user data from Cognito or stored tokens
  useEffect(() => {
    console.log("ProfileSetup: Initializing form data");
    console.log("ProfileSetup: user from useAuthenticator:", user);

    let email = "";
    let firstName = "";
    let lastName = "";
    let displayName = "";

    if (user) {
      // If we have a user from useAuthenticator, use that
      email = user.signInDetails?.loginId || "";
      displayName = email.split("@")[0] || "";
      console.log("ProfileSetup: Using user from useAuthenticator:", {
        email,
        displayName,
      });
    } else {
      // Try to get user info from stored tokens
      const idToken = sessionStorage.getItem("id_token");
      console.log(
        "ProfileSetup: No user from useAuthenticator, checking stored tokens"
      );
      if (idToken) {
        try {
          const payload = JSON.parse(atob(idToken.split(".")[1]));
          email = payload.email || "";
          firstName = payload.given_name || "";
          lastName = payload.family_name || "";
          displayName = payload.name || email.split("@")[0] || "";
          console.log("ProfileSetup: Extracted from stored token:", {
            email,
            firstName,
            lastName,
            displayName,
          });
        } catch (error) {
          console.error("Error decoding stored token:", error);
        }
      } else {
        console.log("ProfileSetup: No stored tokens found");
      }
    }

    if (email) {
      const formDataToSet = {
        username: email.split("@")[0] || "", // Use email prefix as default username
        email,
        firstName,
        lastName,
        displayName,
      };
      console.log("ProfileSetup: Setting form data:", formDataToSet);
      setFormData(formDataToSet);

      // Set Google profile picture
      const googlePicture = getGoogleProfilePicture();
      if (googlePicture) {
        setGoogleProfilePicture(googlePicture);
        console.log("ProfileSetup: Set Google profile picture:", googlePicture);
      }
    } else {
      console.log("ProfileSetup: No email found, form not initialized");
    }
  }, [user]);

  const handleInputChange = (field: keyof ProfileFormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const getGoogleProfilePicture = (): string | null => {
    // Try to get profile picture from stored token
    const idToken = sessionStorage.getItem("id_token");
    if (idToken) {
      try {
        const payload = JSON.parse(atob(idToken.split(".")[1]));
        return payload.picture || null;
      } catch (error) {
        console.error("ProfileSetup: Error decoding stored token:", error);
      }
    }
    return null;
  };

  const saveProfile = async () => {
    console.log("ProfileSetup: saveProfile called");

    setIsLoading(true);

    try {
      // Use Google profile picture if available
      const profilePictureUrl = getGoogleProfilePicture() || undefined;

      // Save user profile to our API/DynamoDB
      const userProfile = {
        username: formData.username,
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        displayName: formData.displayName,
        profilePictureUrl,
        role: UserRole.PLAYER,
        profileCompleted: true,
      };

      console.log("ProfileSetup: About to save user profile:", userProfile);

      const savedUser = await createUser(userProfile);
      console.log("ProfileSetup: createUser result:", savedUser);

      if (savedUser) {
        console.log("User profile saved successfully:", savedUser);
        // Store user info in sessionStorage for the session
        sessionStorage.setItem("userEmail", formData.email);
        sessionStorage.setItem("userDisplayName", formData.displayName);
        navigate("/dashboard", { replace: true });
      } else {
        // If DynamoDB save fails, still allow user to proceed
        console.warn(
          "ProfileSetup: DynamoDB save failed, but allowing user to proceed"
        );
        // Store user info in sessionStorage for the session
        sessionStorage.setItem("userEmail", formData.email);
        sessionStorage.setItem("userDisplayName", formData.displayName);
        navigate("/dashboard", { replace: true });
      }
    } catch (error) {
      console.error("ProfileSetup: Error saving profile:", error);
      // Even if there's an error, allow user to proceed
      sessionStorage.setItem("userEmail", formData.email);
      sessionStorage.setItem("userDisplayName", formData.displayName);
      navigate("/dashboard", { replace: true });
    } finally {
      setIsLoading(false);
    }
  };

  const isFormValid = () => {
    const valid =
      formData.username.trim() !== "" &&
      formData.email.trim() !== "" &&
      formData.firstName.trim() !== "" &&
      formData.lastName.trim() !== "" &&
      formData.displayName.trim() !== "";
    console.log("ProfileSetup: Form validation:", {
      username: formData.username.trim() !== "",
      email: formData.email.trim() !== "",
      firstName: formData.firstName.trim() !== "",
      lastName: formData.lastName.trim() !== "",
      displayName: formData.displayName.trim() !== "",
      isValid: valid,
    });
    return valid;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-primary rounded-full p-3">
              <User className="h-8 w-8 text-primary-foreground" />
            </div>
          </div>
          <CardTitle>Complete Your Profile</CardTitle>
          <CardDescription>
            Set up your profile to get started with MTG League Tracker
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Profile Picture Display */}
          <div className="space-y-2">
            <Label>Profile Picture</Label>
            <div className="flex items-center space-x-4">
              <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                {googleProfilePicture ? (
                  <img
                    src={googleProfilePicture}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Camera className="w-8 h-8 text-gray-400" />
                )}
              </div>
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">
                  Your Google profile picture is automatically used
                </p>
                <p className="text-xs text-muted-foreground">
                  To change your profile picture, update it in your Google
                  account
                </p>
              </div>
            </div>
          </div>

          {/* Username */}
          <div className="space-y-2">
            <Label htmlFor="username">Username *</Label>
            <Input
              id="username"
              value={formData.username}
              onChange={(e) => handleInputChange("username", e.target.value)}
              placeholder="Enter your username"
              required
            />
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              placeholder="Enter your email"
              required
            />
          </div>

          {/* First Name */}
          <div className="space-y-2">
            <Label htmlFor="firstName">First Name *</Label>
            <Input
              id="firstName"
              value={formData.firstName}
              onChange={(e) => handleInputChange("firstName", e.target.value)}
              placeholder="Enter your first name"
              required
            />
          </div>

          {/* Last Name */}
          <div className="space-y-2">
            <Label htmlFor="lastName">Last Name *</Label>
            <Input
              id="lastName"
              value={formData.lastName}
              onChange={(e) => handleInputChange("lastName", e.target.value)}
              placeholder="Enter your last name"
              required
            />
          </div>

          {/* Display Name */}
          <div className="space-y-2">
            <Label htmlFor="displayName">Display Name *</Label>
            <Input
              id="displayName"
              value={formData.displayName}
              onChange={(e) => handleInputChange("displayName", e.target.value)}
              placeholder="How should we display your name?"
              required
            />
          </div>

          {/* Submit Button */}
          <Button
            onClick={saveProfile}
            disabled={!isFormValid() || isLoading}
            className="w-full"
          >
            {isLoading ? "Saving Profile..." : "Complete Profile"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfileSetup;
